import React, { useEffect, useState, useCallback, useRef } from "react";
import { TL } from "../i18n";
import { Link } from "react-router-dom";
import client, { apiErr } from "../lib/api";
import { Button, Card, Badge } from "./ui";
import { ShieldCheck, RotateCcw, HardDriveDownload, X } from "lucide-react";

const APP_LABELS = {
  family_tree: "Family Tree", benefits: "Benefits Management",
  currency: "Currency Management", holdings: "Holdings Management", master: "Master Enabler",
};

function fmt(s) {
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

// Which side is actively recording + seconds left, from a status entry.
function activeSide(entry) {
  const sides = entry?.sides || {};
  if (sides.a?.recording) return { side: "A", left: sides.a.seconds_left };
  if (sides.b?.recording) return { side: "B", left: sides.b.seconds_left };
  return null;
}

// Compact indicator for app/module screens.
export function LiveBackupIndicator({ app }) {
  const [entry, setEntry] = useState(null);
  const [left, setLeft] = useState(0);
  const timer = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/live-buffer/status");
      const e = (data.apps || []).find((a) => a.app === app);
      setEntry(e || null);
      const act = activeSide(e);
      setLeft(act ? act.left : 0);
    } catch { /* ignore */ }
  }, [app]);

  useEffect(() => {
    load();
    const poll = setInterval(load, 30000);
    return () => clearInterval(poll);
  }, [load]);

  useEffect(() => {
    clearInterval(timer.current);
    timer.current = setInterval(() => setLeft((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(timer.current);
  }, [entry]);

  if (!entry) return null;
  const act = activeSide(entry);
  const on = entry.enabled;
  return (
    <Link to="/app/sync" data-testid={`live-backup-indicator-${app}`}
      title={TL("Rolling live backup (crash protection) — manage in Sync & Backup")}
      className="inline-flex items-center gap-1.5 rounded-full border border-black/10 dark:border-white/10 px-2.5 py-1 text-xs lift">
      <span className={`inline-block h-2 w-2 rounded-full ${on && act ? "bg-emerald-500 animate-pulse" : on ? "bg-amber-400" : "bg-black/25 dark:bg-white/25"}`} />
      {on ? (act ? <>{TL("Live backup")} · {act.side} · {fmt(left)}</> : TL("Live backup · standby")) : TL("Live backup off")}
    </Link>
  );
}

// Full control panel for the Sync & Backup page.
export function LiveBackupPanel() {
  const [apps, setApps] = useState([]);
  const [sweepAt, setSweepAt] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState("");
  const [details, setDetails] = useState({});
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try { const { data } = await client.get("/live-buffer/status"); setApps(data.apps || []); setSweepAt(data.last_sweep_at || null); }
    catch (e) { setMsg(apiErr(e)); }
  }, []);
  useEffect(() => { load(); const p = setInterval(load, 30000); return () => clearInterval(p); }, [load]);
  useEffect(() => { const t = setInterval(() => setTick((v) => v + 1), 1000); return () => clearInterval(t); }, []);

  const toggle = async (app, enabled) => {
    setBusy(app);
    try { await client.post("/live-buffer/toggle", { app, enabled }); await load(); }
    catch (e) { setMsg(apiErr(e)); } finally { setBusy(""); }
  };
  const recover = async (app) => {
    if (!window.confirm(`${TL("Recover")} ${TL(APP_LABELS[app])} ${TL("from the live buffer? Recent recorded entries will be re-applied (safe to repeat).")}`)) return;
    setBusy(app);
    try {
      const { data } = await client.post(`/live-buffer/recover/${app}`);
      setMsg(`${APP_LABELS[app]}: restored ${data.restored} entr${data.restored === 1 ? "y" : "ies"}${data.removed ? `, removed ${data.removed}` : ""} from the live buffer.`);
      await load();
    } catch (e) { setMsg(apiErr(e)); } finally { setBusy(""); }
  };
  const toggleDetails = async (app) => {
    if (open === app) { setOpen(""); return; }
    setOpen(app);
    try { const { data } = await client.get(`/live-buffer/recover/${app}`); setDetails((d) => ({ ...d, [app]: data.items || [] })); }
    catch { setDetails((d) => ({ ...d, [app]: [] })); }
  };
  const recoverItem = async (app, it) => {
    setBusy(`${app}:${it.item_id}`);
    try {
      const { data } = await client.post(`/live-buffer/recover/${app}/item`, { coll: it.coll, item_id: it.item_id });
      setMsg(`${itemLabel(it)}: ${data.op === "delete" ? "removed" : "restored"} from the live buffer.`);
      await load();
    } catch (e) { setMsg(apiErr(e)); } finally { setBusy(""); }
  };
  const itemLabel = (it) => {
    const names = { persons: "Person", benefits: "Benefit", currencies: "Currency", holdings: "Holding", transactions: "Transaction", licenses: "License" };
    return `${names[it.coll] || it.coll} ${String(it.item_id || "").slice(0, 8)}`;
  };

  return (
    <Card className="space-y-4" data-testid="live-backup-panel">
      <div className="flex items-center gap-2 font-head"><ShieldCheck size={18} className="text-[var(--primary)]" /> {TL("Rolling live backup (crash & outage protection)")}</div>
      <p className="text-sm opacity-70 max-w-3xl">{TL("Two alternating buffers continuously record your entries so nothing is lost in a crash or power cut. Side A records for the first 35 minutes of each hourly cycle; Side B overlaps from the 30-minute mark — so one is always recording. Each Runner can turn this on or off per application.")}</p>
      {msg && <div className="rounded-md bg-black/5 dark:bg-white/10 p-2 text-sm" data-testid="live-backup-msg">{msg}</div>}
      {apps.length === 0 && <div className="text-sm opacity-60" data-testid="live-backup-empty">{TL("No installed applications yet. Live backup will appear here once you activate an application.")}</div>}
      <div className="divide-y divide-black/5 dark:divide-white/5">
        {apps.map((e) => {
          const act = activeSide(e);
          return (
            <div key={e.app} data-testid={`live-backup-row-${e.app}`}>
            <div className="flex flex-wrap items-center gap-3 py-3">
              <div className="min-w-[160px]">
                <div className="font-medium">{TL(e.label || APP_LABELS[e.app])}</div>
                <div className="text-xs opacity-60">
                  {e.enabled
                    ? (act ? <>{TL("Recording · Side")} {act.side} · {fmt(act.left)} {TL("left")}</> : TL("Enabled · standby (other side covering)"))
                    : TL("Off")}
                  {e.last_recovered_at && <> · {TL("recovered")} {e.last_recovered_count ?? 0} {TL("on")} {new Date(e.last_recovered_at).toLocaleString()}</>}
                  {!e.enabled && e.last_disabled_at && <> · {TL("off since")} {new Date(e.last_disabled_at).toLocaleString()}</>}
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${e.sides?.a?.recording ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-black/5 dark:bg-white/10 opacity-60"}`}>A {e.sides?.a?.recording ? fmt(e.sides.a.seconds_left) : "—"}</span>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${e.sides?.b?.recording ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-black/5 dark:bg-white/10 opacity-60"}`}>B {e.sides?.b?.recording ? fmt(e.sides.b.seconds_left) : "—"}</span>
                <button onClick={() => toggleDetails(e.app)} disabled={e.buffered_events === 0} data-testid={`live-backup-details-${e.app}`}
                  className="inline-flex items-center gap-1 rounded-full bg-black/5 dark:bg-white/10 px-2 py-0.5 disabled:opacity-40 hover:opacity-80">
                  <HardDriveDownload size={11} /> {e.buffered_events} {TL("buffered")} {e.buffered_events > 0 && (open === e.app ? "▲" : "▼")}
                </button>
              </div>
              <div className="ms-auto flex items-center gap-2">
                <Button variant="ghost" onClick={() => recover(e.app)} disabled={busy === e.app || e.buffered_events === 0} data-testid={`live-backup-recover-${e.app}`}>
                  <RotateCcw size={14} /> {TL("Recover")}
                </Button>
                <button type="button" role="switch" aria-checked={e.enabled} onClick={() => toggle(e.app, !e.enabled)}
                  disabled={busy === e.app} data-testid={`live-backup-toggle-${e.app}`}
                  className={`relative h-6 w-11 rounded-full transition-colors ${e.enabled ? "bg-emerald-500" : "bg-black/20 dark:bg-white/20"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${e.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
            </div>
            {open === e.app && (
              <div className="mb-3 rounded-md bg-black/5 dark:bg-white/10 p-3 text-xs" data-testid={`live-backup-items-${e.app}`}>
                {(details[e.app] || []).length === 0 ? <span className="opacity-60">{TL("No recoverable items.")}</span> : (
                  <ul className="space-y-1">
                    {(details[e.app] || []).map((it, i) => (
                      <li key={i} className="flex flex-wrap items-center gap-2" data-testid={`live-backup-item-${e.app}-${i}`}>
                        <span className={`rounded px-1.5 py-0.5 ${it.op === "delete" ? "bg-[#DA1E28]/15 text-[#DA1E28]" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"}`}>{it.op}</span>
                        <span className="font-medium">{itemLabel(it)}</span>
                        {it.actor && <span className="opacity-70">by {it.actor}</span>}
                        <span className="ms-auto opacity-50">{it.ts ? new Date(it.ts).toLocaleTimeString() : ""}</span>
                        <button onClick={() => recoverItem(e.app, it)} disabled={busy === `${e.app}:${it.item_id}`}
                          data-testid={`live-backup-item-recover-${e.app}-${i}`}
                          className="inline-flex items-center gap-1 rounded-full border border-black/15 dark:border-white/15 px-2 py-0.5 hover:bg-black/5 dark:hover:bg-white/10">
                          <RotateCcw size={11} /> {TL("Recover")}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            </div>
          );
        })}
      </div>
      {sweepAt && <div className="text-xs opacity-50" data-testid="live-backup-sweep">{TL("Last buffer sweep (expired buffers cleared):")} {new Date(sweepAt).toLocaleString()}</div>}
    </Card>
  );
}

// Session-scoped auto-prompt shown after startup if a surviving buffer exists.
export function RecoverBanner() {
  const [show, setShow] = useState(false);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (sessionStorage.getItem("recover_banner_dismissed")) return;
    client.get("/live-buffer/status").then(({ data }) => {
      if ((data.total_recoverable || 0) > 0) { setCount(data.total_recoverable); setShow(true); }
    }).catch(() => {});
  }, []);
  const dismiss = () => { sessionStorage.setItem("recover_banner_dismissed", "1"); setShow(false); };
  if (!show) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-[var(--primary)]/40 bg-[var(--primary)]/10 px-4 py-3 text-sm" data-testid="recover-banner">
      <ShieldCheck size={18} className="text-[var(--primary)]" />
      <span>{TL("Live backup is protecting")} <b>{count}</b> {count === 1 ? TL("recent entry.") : TL("recent entries.")} {TL("If this app restarted after a crash or power cut, you can recover them.")}</span>
      <div className="ms-auto flex items-center gap-2">
        <Link to="/app/sync" onClick={dismiss}><Button data-testid="recover-banner-review"><RotateCcw size={14} /> {TL("Review & recover")}</Button></Link>
        <button onClick={dismiss} data-testid="recover-banner-dismiss" className="rounded-full p-1 opacity-60 hover:opacity-100"><X size={16} /></button>
      </div>
    </div>
  );
}
